import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tokens } from '@companycam/slab-tokens';

import Modal from './Modal';

import {
  measurementToStringMetric,
  measurementToStringImperial,
  parseMeasurement,
} from '../utils/strings';
import {
  UNITS_NONE,
  UNITS_IMPERIAL,
  UNITS_METRIC,
} from '../constants/textModes';

const RowTabs = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  align-self: flex-start;
  overflow: hidden;
  border-radius: 4px;
  border: solid 1px ${tokens.color_text_subtle_le};
`;

const RowInputs = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 56px;
  margin: 16px 0;
  width: 100%;
`;

const TabItem = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${(props) =>
    props.selected
      ? tokens.color_background_1_le
      : tokens.color_text_subtle_le};
  background-color: ${(props) =>
    props.selected
      ? tokens.color_text_subtle_le
      : tokens.color_background_1_le};
  text-decoration: none;
  font-family: sans-serif;
  width: 56px;
  height: 16px;
  padding: 8px 0;
`;

const TabItemFirst = styled(TabItem)`
  border-right: 1px solid ${tokens.color_text_subtle_le};
`;

const TabItemLast = styled(TabItem)`
  border-left: 1px solid ${tokens.color_text_subtle_le};
`;

const TextMultiLine = styled.textarea`
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 12px;

  resize: none;
  border-radius: 4px;
  border-color: ${tokens.color_background_3_le};

  font-size: 14px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: ${tokens.color_label_dark_gray};
  line-height: 1;

  :focus {
    outline: 0;
    border-color: ${tokens.color_label_light_gray};
  }
`;

const TextSingleLine = styled.input`
  font-family: sans-serif;
  width: 96px;
  padding: 12px;
  border-width: 1px;
  border-radius: 4px;
  border-style: solid;
  border-color: ${tokens.color_background_3_le};
  font-size: 14px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: ${tokens.color_label_dark_gray};
  line-height: 1;

  :focus {
    outline: 0;
    border-color: ${tokens.color_label_light_gray};
  }
`;

const TextLabel = styled.span`
  width: 24px;
  padding: 0 16px 0 8px;
  font-family: sans-serif;
`;

class TextInput extends Component {
  constructor(props) {
    super(props);

    const fulltext = props.initialLines.join('\n');
    const {
      units = UNITS_NONE,
      valueFt = 0,
      valueIn = 0,
      valueM = 0,
      valueCm = 0,
    } = parseMeasurement(fulltext);

    this.state = {
      units,
      valueFt,
      valueIn,
      valueM,
      valueCm,
      valueTextarea: units === UNITS_NONE ? fulltext : '',
    };
  }

  onChangeUnit = ({ target: { name, value } }) => {
    this.setState({ [name]: value });
  };

  onSave = () => {
    switch (this.state.units) {
      case UNITS_IMPERIAL: {
        const { valueFt, valueIn } = this.state;
        const formattedLine = measurementToStringImperial(valueFt, valueIn);
        this.props.onSave([formattedLine]);
        break;
      }
      case UNITS_METRIC: {
        const { valueM, valueCm } = this.state;
        const formattedLine = measurementToStringMetric(valueM, valueCm);
        this.props.onSave([formattedLine]);
        break;
      }
      case UNITS_NONE:
      default: {
        const { valueTextarea } = this.state;
        this.props.onSave(valueTextarea.split('\n'));
      }
    }
    this.props.onCancel();
  };

  renderTextInput() {
    switch (this.state.units) {
      case UNITS_NONE:
        return (
          <TextMultiLine
            autoFocus
            className="CCEditorTextInput"
            name="valueTextarea"
            type="text"
            value={this.state.valueTextarea}
            onChange={this.onChangeUnit}
          />
        );
      case UNITS_IMPERIAL:
        return (
          <>
            <TextSingleLine
              autoFocus
              className="CCEditorTextInput"
              name="valueFt"
              type="text"
              value={this.state.valueFt}
              onChange={this.onChangeUnit}
            />
            <TextLabel>ft.</TextLabel>
            <TextSingleLine
              className="CCEditorTextInput"
              name="valueIn"
              type="text"
              value={this.state.valueIn}
              onChange={this.onChangeUnit}
            />
            <TextLabel>in.</TextLabel>
          </>
        );
      case UNITS_METRIC:
        return (
          <>
            <TextSingleLine
              autoFocus
              className="CCEditorTextInput"
              name="valueM"
              type="text"
              value={this.state.valueM}
              onChange={this.onChangeUnit}
            />
            <TextLabel>m.</TextLabel>
            <TextSingleLine
              className="CCEditorTextInput"
              name="valueCm"
              type="text"
              value={this.state.valueCm}
              onChange={this.onChangeUnit}
            />
            <TextLabel>cm.</TextLabel>
          </>
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <Modal
        title="Add Text"
        onClickCancel={this.props.onCancel}
        onClickPrimary={this.onSave}
        primaryButtonText="Save"
        cancelButtonText="Cancel"
      >
        <>
          <RowTabs>
            <TabItemFirst
              href="#"
              selected={this.state.units === UNITS_NONE}
              onClick={(e) => {
                e.preventDefault();
                this.setState({ units: UNITS_NONE });
              }}
            >
              Abc
            </TabItemFirst>
            <TabItem
              href="#"
              selected={this.state.units === UNITS_IMPERIAL}
              onClick={(e) => {
                e.preventDefault();
                this.setState({ units: UNITS_IMPERIAL });
              }}
            >
              ft. in.
            </TabItem>
            <TabItemLast
              href="#"
              selected={this.state.units === UNITS_METRIC}
              onClick={(e) => {
                e.preventDefault();
                this.setState({ units: UNITS_METRIC });
              }}
            >
              m. cm.
            </TabItemLast>
          </RowTabs>
          <RowInputs>{this.renderTextInput()}</RowInputs>
        </>
      </Modal>
    );
  }
}

TextInput.propTypes = {
  initialLines: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

TextInput.defaultProps = {
  initialLines: [''],
  onCancel: () => {},
  onSave: () => {},
};

export default TextInput;
